import { getDynamicPricingExperimentEntryStatus, type DynamicPricingExperimentEntryStatus } from 'api/recommendations'
import { isEligibleForLereDynamicPricingAbTest } from 'checkout/lib/utils/lereDynamicPricing'
import { LabFeatureFlags } from 'constants/lab'
import type { LereDynamicPricingGroup } from './types'

export async function getLereDynamicPricingExperimentStatus(memberId?: string, domainUserId?: string) {
  if (!isEligibleForLereDynamicPricingAbTest() || (!memberId && !domainUserId)) {
    return undefined
  }
  return getDynamicPricingExperimentEntryStatus(memberId, domainUserId)
}

/** General flags use the global tracking identity; LERE is evaluated separately. */
export function getGeneralLabFeatureFlagKeys(configuredKeys: ReadonlyArray<string>): Array<string> {
  return configuredKeys.filter(key => key !== LabFeatureFlags.enableLereDynamicPricingGroup)
}

export function hasRecordedLereExperimentEntry(status?: DynamicPricingExperimentEntryStatus): boolean {
  return !!(status?.triggered_variant || status?.domain_triggered_variant)
}

/** An existing device assignment takes precedence over asynchronously recorded exposure history. */
export function hasConflictingLereExperimentVariants(
  status: DynamicPricingExperimentEntryStatus,
  deviceGroup: LereDynamicPricingGroup | undefined = status.domain_triggered_variant ?? undefined,
): boolean {
  return !!deviceGroup && !!status.triggered_variant && deviceGroup !== status.triggered_variant
}

/** Returns the previously triggered variant corresponding to the domain user id if no member assignment has been recorded yet. */
export function getLereDomainOnlyExperimentGroup(status: DynamicPricingExperimentEntryStatus): LereDynamicPricingGroup | undefined {
  if (status.triggered_variant || status.is_multi_variant) return undefined
  return status.domain_triggered_variant ?? undefined
}

/** Exclusion is tied to the LE member identity, never to an anonymous domain identity. */
export function isLereMemberExcluded(labFlags: App.LabFlags, memberId?: string): boolean {
  return !!memberId && labFlags[LabFeatureFlags.enableLereDynamicPricingGroup]?.excludedMemberId === memberId
}

export function getLereMemberEligibilityFlag(
  status: DynamicPricingExperimentEntryStatus,
  memberId?: string,
  existingFlag?: App.LabFeatureFlagResult,
): App.LabFeatureFlagResult | undefined {
  if (!memberId) return existingFlag
  // Once discovered, exclusion cannot be undone by an older in-flight status response.
  if (existingFlag?.excludedMemberId === memberId) return existingFlag

  const key = LabFeatureFlags.enableLereDynamicPricingGroup
  if (status.exclude_from_new_experiment) {
    // Replace the entire assignment: exclusion also overrides any anonymous exposure in Redux.
    return { key, enabled: false, eligibilityCheckedMemberId: memberId, excludedMemberId: memberId }
  }
  return { key, enabled: false, ...existingFlag, eligibilityCheckedMemberId: memberId, excludedMemberId: undefined }
}
